/**
 * Le tre colonne del Cruscotto Commesse — **pronte · pianificate o parziali ·
 * non programmate** — raggruppate per cliente (ridisegno del 23-09-2026, chiesto
 * dalla direzione).
 *
 * La domanda a cui rispondono è «a che punto siamo, cliente per cliente», e la
 * risposta deve stare in un colpo d'occhio: il badge `5/13` dice quante commesse
 * di quel cliente stanno in questa colonna su quante ne ha aperte in tutto. Il
 * denominatore è lo stesso nelle tre colonne, quindi i tre numeratori sommano al
 * totale del cliente — e la riga in fondo dichiara che le tre colonne coprono
 * tutto il portafoglio.
 *
 * ⚠️ Le colonne leggono **solo l'asse disponibilità** (la merce c'è, è
 * pianificata, manca), non la data di consegna del gestionale, che il 16-08 è
 * stata dichiarata «per lo più fittizia». È la ragione per cui non riusano i
 * quattro stati del motore: quelli mescolano i due assi.
 *
 * Il perché della regola di assegnazione — *il buco vince* — sta in
 * `core/m1/colonne.py`, che è dove la regola è scritta una volta sola.
 */
import {
  AlertTriangle,
  CalendarCheck,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  StickyNote,
  Truck,
} from "lucide-react";
import { useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { useM1ColonneCommesse } from "@/hooks/useM1";
import { num } from "@/lib/numeri";
import { cn } from "@/lib/utils";
import type {
  M1ChiaveColonna,
  M1ClienteInColonna,
  M1ColonnaCommesse,
  M1CommessaInColonna,
} from "@/types/api";

/** Quanti clienti si vedono prima di chiedere il resto.
 *
 *  Il taglio **si dichiara** con un pulsante che dice quanti ne restano: la
 *  troncatura silenziosa del Magazzino («25 su 130», senza dirlo) si leggeva come
 *  «questo è tutto», ed è il difetto trovato costruendo la paginazione il 19-08.
 *  Qui serve davvero: «Non programmate» ha 50 clienti su 74. */
const CLIENTI_VISIBILI = 10;

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function dataBreve(iso: string | null): string {
  if (!iso) return "—";
  const [, m, g] = iso.split("-").map(Number);
  return `${g} ${MESI[m - 1]}`;
}

const fmt = (v: string | number) => num(Number(v));

/**
 * Quanto di questa commessa è **già uscito**, secondo il gestionale.
 *
 * È il numero che nessuno può contestare, perché è il loro: `qty_spedita` su
 * `qty_ordinata`, cioè `ritirato` sul totale della barra. Va davanti alla data
 * di uscita, che dipende invece dall'attribuzione a un carico — sulla #339 dice
 * «75%», e da sola spiega l'apparente contraddizione «uscita, però pronta»:
 * sono due pezzi diversi della stessa commessa.
 */
function quotaUscita(m: M1CommessaInColonna): number | null {
  const d = m.dove_la_merce;
  const totale =
    Number(d.ritirato) +
    Number(d.magazzino) +
    Number(d.produzione) +
    Number(d.da_produrre) +
    Number(d.non_valutato);
  if (!totale || Number(d.ritirato) <= 0) return null;
  return Math.round((100 * Number(d.ritirato)) / totale);
}

/** Come si chiama, in pagina, la forza di un'attribuzione. */
const FONTE: Record<string, { etichetta: string; tono: string }> = {
  manuale: {
    etichetta: "confermato da Marina",
    tono: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-300",
  },
  documento: {
    etichetta: "dal documento",
    tono: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-300",
  },
  lettura: {
    // ⚠️ Tono neutro e parola esplicita: una lettura non deve *sembrare* un
    // fatto. È la differenza fra una riga che regge a un controllo e una che
    // dà l'appiglio per dire che la pagina non funziona.
    etichetta: "da un'email, non confermato",
    tono: "bg-muted text-muted-foreground",
  },
};

/**
 * Titolo, spiegazione e colore di ciascuna colonna.
 *
 * La spiegazione non è decorativa: «pianificate o parziali» non si capisce da
 * sé, e senza la riga sotto il titolo chi guarda si inventa una definizione.
 */
const COLONNE: Record<
  M1ChiaveColonna,
  { titolo: string; sotto: string; bordo: string; testa: string; numero: string; puntino: string }
> = {
  pronte: {
    titolo: "Pronte",
    sotto: "la merce è a magazzino: potrebbero partire oggi",
    bordo: "border-emerald-300 dark:border-emerald-900",
    testa: "bg-emerald-50 dark:bg-emerald-950/40",
    numero: "text-emerald-700 dark:text-emerald-400",
    puntino: "bg-emerald-600 dark:bg-emerald-500",
  },
  pianificate: {
    titolo: "Pianificate o parziali",
    sotto: "quello che manca è in produzione: tutto è coperto",
    bordo: "border-amber-300 dark:border-amber-900",
    testa: "bg-amber-50 dark:bg-amber-950/40",
    numero: "text-amber-700 dark:text-amber-400",
    puntino: "bg-amber-500 dark:bg-amber-400",
  },
  non_programmate: {
    titolo: "Non programmate",
    sotto: "manca almeno un pezzo che nessuno ha ancora pianificato",
    bordo: "border-rose-300 dark:border-rose-900",
    testa: "bg-rose-50 dark:bg-rose-950/40",
    numero: "text-rose-700 dark:text-rose-400",
    puntino: "bg-rose-700/80 dark:bg-rose-500/80",
  },
};

export function ColonneCommesse({
  onApriCommessa,
}: {
  onApriCommessa: (numero: number) => void;
}) {
  const { data, isLoading } = useM1ColonneCommesse();

  if (isLoading) {
    return (
      <div className="grid gap-3 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-64 w-full" />
        ))}
      </div>
    );
  }

  if (!data?.colonne.length) {
    return (
      <section className="rounded-lg border border-dashed bg-card/40 px-6 py-10 text-center">
        <p className="text-sm text-muted-foreground">
          Nessuna commessa aperta: manca lo snapshot degli ordini.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <div className="grid items-start gap-3 lg:grid-cols-3">
        {data.colonne.map((col) => (
          <Colonna key={col.chiave} colonna={col} onApriCommessa={onApriCommessa} />
        ))}
      </div>

      {/* La partizione **si dichiara**. Tre numeri che non dicono se coprono
          tutto il portafoglio si leggono come una selezione — stessa ragione per
          cui il lead time, qui sotto, scrive il suo denominatore. */}
      <p className="text-[11px] text-muted-foreground">
        {data.colonne.map((c) => fmt(c.n_commesse)).join(" + ")} ={" "}
        <b className="text-foreground">{fmt(data.n_commesse_aperte - data.n_commesse_non_valutate)}</b>{" "}
        commesse aperte su {fmt(data.n_commesse_aperte)}: ogni commessa sta in una colonna
        sola, e il conto di un cliente si legge nelle tre colonne insieme.
        {data.n_commesse_non_valutate > 0 && (
          <>
            {" "}
            Le altre <b>{fmt(data.n_commesse_non_valutate)}</b> sono fatte solo di materiale
            di consumo: il motore non le valuta, quindi non hanno una colonna.
          </>
        )}
      </p>
    </section>
  );
}

function Colonna({
  colonna,
  onApriCommessa,
}: {
  colonna: M1ColonnaCommesse;
  onApriCommessa: (numero: number) => void;
}) {
  const stile = COLONNE[colonna.chiave];
  const [tutti, setTutti] = useState(false);
  const clienti = tutti ? colonna.clienti : colonna.clienti.slice(0, CLIENTI_VISIBILI);
  const nascosti = colonna.clienti.length - clienti.length;

  return (
    <div className={cn("overflow-hidden rounded-lg border bg-card", stile.bordo)}>
      <div className={cn("border-b px-3 py-2", stile.testa, stile.bordo)}>
        <div className="flex items-baseline gap-2">
          <span className={cn("text-2xl font-semibold leading-none tabular-nums", stile.numero)}>
            {fmt(colonna.n_commesse)}
          </span>
          <h3 className="text-[13px] font-semibold">{stile.titolo}</h3>
        </div>
        <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{stile.sotto}</p>
        <p className="mt-1 text-[10.5px] text-muted-foreground">
          {fmt(colonna.qty_residua)} pezzi · {fmt(colonna.n_clienti)}{" "}
          {colonna.n_clienti === 1 ? "cliente" : "clienti"}
        </p>
        {colonna.n_impegnate > 0 && (
          /* ⚠️ Il pezzo che rende onesta la prima colonna: 11 delle 34 «pronte»
             se le è già prese un carico previsto, 7 con la nota di prelievo
             emessa. Senza questa riga la colonna dice «potrebbero partire oggi»
             di merce che è già destinata a qualcuno. */
          <p className="mt-0.5 text-[10.5px] text-muted-foreground">
            <b className="text-foreground">
              {fmt(colonna.n_commesse - colonna.n_impegnate)} libere
            </b>{" "}
            · {fmt(colonna.n_impegnate)} già impegnate da un carico previsto
          </p>
        )}
      </div>

      {colonna.clienti.length === 0 ? (
        <p className="px-3 py-6 text-center text-[11.5px] text-muted-foreground">
          Nessuna commessa in questa colonna.
        </p>
      ) : (
        <ul className="divide-y">
          {clienti.map((c) => (
            <RigaCliente
              key={c.cliente_codice}
              cliente={c}
              chiave={colonna.chiave}
              puntino={stile.puntino}
              onApriCommessa={onApriCommessa}
            />
          ))}
        </ul>
      )}

      {nascosti > 0 && (
        <button
          type="button"
          onClick={() => setTutti(true)}
          className="w-full border-t px-3 py-1.5 text-[11px] text-brand-700 hover:bg-accent/40 dark:text-brand-400"
        >
          altri {fmt(nascosti)} clienti
        </button>
      )}
    </div>
  );
}

function RigaCliente({
  cliente,
  chiave,
  puntino,
  onApriCommessa,
}: {
  cliente: M1ClienteInColonna;
  chiave: M1ChiaveColonna;
  puntino: string;
  onApriCommessa: (numero: number) => void;
}) {
  const [aperto, setAperto] = useState(false);
  const tutte = cliente.n_commesse === cliente.n_commesse_cliente;

  return (
    <li>
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-accent/40"
        aria-expanded={aperto}
      >
        {aperto ? (
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium">
          {cliente.cliente_ragione_sociale}
        </span>
        {/* Il «5/13»: quante ne ha qui su quante ne ha in tutto. Quando sono
            tutte, il tondo pieno lo dice senza dover leggere due numeri. */}
        <span
          className={cn(
            "shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
            tutte ? "bg-foreground text-background" : "bg-muted text-foreground",
          )}
          title={
            tutte
              ? `Tutte e ${cliente.n_commesse_cliente} le commesse aperte di questo cliente stanno qui`
              : `${cliente.n_commesse} commesse qui, su ${cliente.n_commesse_cliente} aperte in tutto il portafoglio`
          }
        >
          {cliente.n_commesse}/{cliente.n_commesse_cliente}
        </span>
        {cliente.n_carichi_previsti > 0 && (
          /* I carichi del **cliente**, non solo quelli agganciati a queste
             commesse: il legame per commessa lo ricava Aida dalle email ed è
             parziale, questo conteggio copre di più. */
          <span
            className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-sky-100 px-1.5 py-px text-[10px] font-medium text-sky-900 dark:bg-sky-950 dark:text-sky-300"
            title={`${cliente.n_carichi_previsti} carichi previsti aperti in Spedizioni${
              cliente.n_carichi_ndp ? `, di cui ${cliente.n_carichi_ndp} con la NDP emessa` : ""
            }`}
          >
            <Truck className="size-2.5" />
            {cliente.n_carichi_previsti}
            {cliente.n_carichi_ndp > 0 && <span className="font-semibold">·NDP</span>}
          </span>
        )}
        <span
          className="w-16 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground"
          title="Pezzi ancora da consegnare, per le sue commesse di questa colonna"
        >
          {fmt(cliente.qty_residua)}
        </span>
      </button>

      {aperto && (
        <ul className="border-t bg-muted/20">
          {cliente.commesse.map((c) => (
            <RigaCommessa
              key={c.numero_ordine}
              commessa={c}
              chiave={chiave}
              puntino={puntino}
              onApri={() => onApriCommessa(c.numero_ordine)}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/** La riga della singola commessa: numero, quanto pesa, e la cosa che conta
 *  **in questa colonna** — che non è la stessa nelle tre. */
function RigaCommessa({
  commessa: c,
  chiave,
  puntino,
  onApri,
}: {
  commessa: M1CommessaInColonna;
  chiave: M1ChiaveColonna;
  puntino: string;
  onApri: () => void;
}) {
  const imp = c.impegno;
  const impegnata = !!imp && imp.n_carichi_vivi > 0;
  // ⚠️ La distinzione che rende onesto tutto il resto: una data di ritiro passata
  // che viene da un carico **già uscito** non è un ritiro scaduto. WONDERFOOD
  // ritira a rate, quattro dei suoi cinque carichi sono partiti — l'ultimo il 17
  // settembre — e fino a ieri qui c'erano cinque badge rossi «ritiro scaduto 17
  // set» su commesse la cui merce quel giorno era uscita.
  const soloUscita = !!imp && imp.n_carichi_vivi === 0 && !!imp.uscito_il;
  const dataRitiro = impegnata ? imp!.data_ritiro : c.ritiro_fissato_il;
  const quota = quotaUscita(c);
  return (
    <li>
      <button
        type="button"
        onClick={onApri}
        className="flex w-full items-start gap-2 px-3 py-1.5 pl-8 text-left hover:bg-accent/50"
        title="Apre il dettaglio della commessa"
      >
        <span className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", puntino)} aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="font-mono text-[11.5px] font-semibold">#{c.numero_ordine}</span>
            {c.stato_ritiro === "tassativa" && !soloUscita && (
              /* «Pronta + ritiro fissato» è la coppia più azionabile del modulo,
                 e non si vedeva da nessuna parte: 15 delle 34 pronte. */
              <span
                className={cn(
                  "inline-flex items-center gap-0.5 rounded-full px-1.5 py-px text-[10px] font-medium",
                  c.ritiro_scaduto
                    ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
                    : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
                )}
                title={
                  c.ritiro_scaduto
                    ? "Il ritiro era fissato e la data è passata, e il carico è ancora aperto: o la merce è uscita senza chiudere la commessa, o il ritiro è saltato"
                    : dataRitiro
                      ? "Il ritiro è già concordato col cliente"
                      : "Il ritiro è in corso (NDP emessa), ma il campo della data è vuoto: non la sappiamo, e non si inventa"
                }
              >
                <CalendarCheck className="size-2.5" />
                {/* ⚠️ «ritiro —» non voleva dire niente, ed era il caso più
                    frequente dopo quello con la data: 31 avvisi su 59 hanno la
                    NDP emessa e il campo della data vuoto. Il ritiro **è**
                    fissato, la data non la sappiamo: si dice così. */}
                {/* ⚠️ La data la dà **il carico vivo**, non `ritiro_fissato_il`:
                    quella è la più vicina fra tutti gli avvisi che citano la
                    commessa, compresi quelli già partiti. Sulla #121 di
                    WONDERFOOD diceva «ritiro 17 set» — la data del carico
                    uscito — mentre la NDP viva una data non ce l'ha ancora (ed
                    è per questo che da oggi Aida la chiede a Marina). */}
                {dataRitiro
                  ? `ritiro ${c.ritiro_scaduto ? "scaduto " : ""}${dataBreve(dataRitiro)}`
                  : "ritiro fissato"}
              </span>
            )}
            {c.stato_ritiro === "a_chiamata" && (
              <span
                className="rounded-full bg-orange-100 px-1.5 py-px text-[10px] font-medium text-orange-900 dark:bg-orange-950 dark:text-orange-300"
                title="Il cliente ritira quando gli serve: la merce resta nel nostro magazzino"
              >
                a chiamata
              </span>
            )}
            {c.piano_scaduto && (
              /* Il piano c'è — quindi la commessa resta qui — ma la sua data è
                 passata e la merce non è arrivata: senza il segno si legge come
                 merce in arrivo. */
              <span
                className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-300"
                title={`Il piano doveva finire il ${dataBreve(c.pronta_il)} e la merce non è arrivata`}
              >
                <AlertTriangle className="size-2.5" />
                piano scaduto
              </span>
            )}
            {impegnata && (
              /* Il carico previsto che se l'è già presa, **con la sua fonte**:
                 «NDP 1214» si controlla in un secondo, «da un'email» no. Il
                 numero della nota è il riferimento con cui ne parla Marina. */
              <span
                className={cn(
                  "inline-flex items-center gap-0.5 rounded-full px-1.5 py-px text-[10px] font-medium",
                  FONTE[imp!.vale]?.tono ?? FONTE.lettura.tono,
                )}
                title={
                  (imp!.ha_ndp
                    ? "Nota di prelievo già emessa: questa merce è destinata a un carico"
                    : `Carico previsto in Spedizioni, stato «${imp!.stato.replace(/_/g, " ")}»`) +
                  ` — attribuzione ${FONTE[imp!.vale]?.etichetta ?? imp!.vale}` +
                  (imp!.n_carichi_vivi > 1 ? ` · carichi aperti: ${imp!.n_carichi_vivi}` : "") +
                  (imp!.pallet ? ` · ${fmt(imp!.pallet)} pallet` : "")
                }
              >
                <Truck className="size-2.5" />
                {imp!.ndp_numero
                  ? `NDP ${imp!.ndp_numero}`
                  : imp!.ha_ndp
                    ? "NDP emessa"
                    : "carico previsto"}
                {imp!.vale === "lettura" && <span className="font-normal"> ?</span>}
              </span>
            )}
            {soloUscita && (
              /* Non è un impegno ed è per questo che ha un colore neutro: è il
                 contesto che spiega perché la commessa è ancora aperta — il
                 cliente ritira a rate. */
              <span
                className="inline-flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-px text-[10px] font-medium text-muted-foreground"
                title={
                  `Una parte di questa commessa è uscita${
                    imp!.ndp_uscita ? ` con la nota di prelievo ${imp!.ndp_uscita}` : ""
                  }: il resto non ha un ritiro fissato. Attribuzione ${
                    FONTE[imp!.vale_uscita ?? "lettura"]?.etichetta ?? ""
                  }`
                }
              >
                <CheckCheck className="size-2.5" />
                uscita {dataBreve(imp!.uscito_il)}
                {imp!.ndp_uscita ? ` · NDP ${imp!.ndp_uscita}` : ""}
                {imp!.vale_uscita === "lettura" && " ?"}
              </span>
            )}
            {c.note_memoria > 0 && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground"
                title={`${c.note_memoria} note dei commerciali su questa commessa o sul cliente`}
              >
                <StickyNote className="size-2.5" />
                {c.note_memoria}
              </span>
            )}
          </span>
          <span className="mt-0.5 block text-[10.5px] leading-snug text-muted-foreground">
            {chiave === "pronte" && (
              <>
                {/* ⚠️ Il dato del gestionale **per primo**: «già uscito il 75%»
                    è il loro numero e non si discute, e da solo spiega perché
                    una commessa può essere insieme «uscita» e «pronta» — sono
                    due pezzi diversi. La data e la NDP vengono dopo, come
                    dettaglio da controllare. */}
                {quota !== null && <>già uscito il {quota}% · </>}
                {impegnata ? (
                  <>
                    il resto è destinato a un carico
                    {imp!.data_ritiro ? <> · ritiro {dataBreve(imp!.data_ritiro)}</> : null}
                  </>
                ) : (
                  <>pronti da spedire · consegna {dataBreve(c.data_consegna_minima)}</>
                )}
              </>
            )}
            {chiave === "pianificate" && (
              <>
                {Number(c.dove_la_merce.magazzino) > 0 && (
                  <>{fmt(c.dove_la_merce.magazzino)} già pronti · </>
                )}
                completa il {dataBreve(c.pronta_il)}
              </>
            )}
            {chiave === "non_programmate" && (
              <>
                mancano <b className="text-rose-700 dark:text-rose-400">{fmt(c.qty_mancante)}</b> pz
                su {c.articoli_mancanti.length}{" "}
                {c.articoli_mancanti.length === 1 ? "articolo" : "articoli"}
                {c.articoli_mancanti.length > 0 && (
                  <span title={c.articoli_mancanti.join(" · ")}> ⓘ</span>
                )}
              </>
            )}
          </span>
        </span>
        <span className="shrink-0 text-right text-[11px] font-medium tabular-nums">
          {fmt(c.qty_residua)}
        </span>
      </button>
    </li>
  );
}
